import React, { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Alert, Paper, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { gql, useMutation } from '@apollo/client';
import { useYupObject } from 'hooks';
import { tokenAtom } from 'atoms';
import { checkPasswordStrength } from 'services/auth';
import PasswordField from 'components/PasswordField';
import StrengthPassword from './StrengthPassword';
import { createApolloClient } from 'services/graphql';

const CHANGE_PASSWORD = gql`
  mutation ChangePassword(
    $email: String!
    $masterCompanyId: Int!
    $password: String
    $newPassword: String!
    $app: String
  ) {
    updatePassword(
      email: $email
      masterCompanyId: $masterCompanyId
      password: $password
      newPassword: $newPassword
      app: $app
    )
  }
`;

function ChangePassword() {
  const location = useLocation();
  const yup = useYupObject();
  const { t } = useTranslation(['_common', 'change_password', 'api_errors']);
  const [error, setError] = useState<Boolean | String>(false);
  const setToken = useSetRecoilState(tokenAtom);
  const navigate = useNavigate();
  // @ts-ignore
  const token = location?.state?.token || '';

  const [strengthPasswordErrors, setStrengthPasswordErrors] = useState(checkPasswordStrength(''));

  const [changePassword, { error: errorApi, loading }] = useMutation(CHANGE_PASSWORD, {
    client: createApolloClient(token),
    onCompleted: () => {
      setToken(token);
      navigate('/', { replace: true });
    },
  });

  const validationSchema = yup.object().shape({
    password: yup
      .string()
      .required('empty_password')
      .min(6, 'min_password')
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[ !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~\\])/),
    passwordConfirm: yup
      .string()
      .required()
      .oneOf([yup.ref('password'), null], 'must_match'),
  });

  const initialValues = {
    password: '',
    passwordConfirm: '',
  };

  const formik = useFormik({
    validationSchema,
    initialValues,
    onSubmit: ({ password: newpassword }) => {
      setError(false);
      changePassword({
        variables: {
          app: 'br.com.segware.mysecurity',
          // @ts-ignore
          email: location?.state?.email || '',
          // @ts-ignore
          masterCompanyId: location?.state?.masterCompanyId || 0,
          // @ts-ignore
          password: location?.state?.password || '',
          newPassword: newpassword,
        },
      });
    },
  });

  return (
    <Paper square elevation={3} component="form" onSubmit={formik.handleSubmit} sx={{ p: 5 }}>
      <Typography variant="h6">{t('change_password:change_your_password')}</Typography>
      <PasswordField
        label={t('change_password:new_password')}
        name="password"
        id="password"
        autoFocus
        fullWidth
        value={formik.values.password}
        onChange={(e) => {
          setStrengthPasswordErrors(checkPasswordStrength(e.target.value || ''));
          formik.handleChange(e);
        }}
        margin="normal"
        sx={{ mr: 3 }}
        autoComplete="new-password"
      />

      <StrengthPassword
        t={t}
        checkResult={strengthPasswordErrors}
        password={formik.values.password}
      />

      <PasswordField
        label={t('change_password:new_password_confirm')}
        name="passwordConfirm"
        id="passwordConfirm"
        autoFocus
        fullWidth
        helperText={
          formik.errors.passwordConfirm && formik.errors.passwordConfirm === 'must_match'
            ? t(`change_password:must_match`)
            : // @ts-ignore
              t(formik.errors.passwordConfirm)
        }
        value={formik.values.passwordConfirm}
        onChange={formik.handleChange}
        error={Boolean(formik.errors.passwordConfirm)}
        margin="normal"
        sx={{ mr: 3 }}
        autoComplete="new-password"
        disabled={strengthPasswordErrors?.hasFragility}
        onBlur={formik.handleBlur}
      />

      {(error || errorApi) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {t(
            `api_errors:${(error || errorApi) as 'invalid_username_or_password'}`,
            t('api_errors:generic_error'),
          )}
        </Alert>
      )}

      <LoadingButton
        loading={loading}
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        sx={{ mb: 2, mt: 4 }}
      >
        {t('_common:confirm')}
      </LoadingButton>
    </Paper>
  );
}

export default ChangePassword;
